import * as Sentry from '@sentry/react';
import localforage from 'localforage';

async function getStorageEstimate() {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const { quota, usage } = await navigator.storage.estimate();
      return {
        quotaMB: Math.round(quota / 1024 / 1024),
        usageMB: Math.round(usage / 1024 / 1024),
        usagePercent: quota ? Math.round((usage / quota) * 100) : null,
      };
    }
  } catch (_) {}
  return null;
}

export const reportSyncError = async (err, syncType, networkState) => {
  const statusCode = err.networkError?.statusCode;
  const errorMessage = err.message || '';

  if (statusCode === 400) {
    if (errorMessage.includes('Firebase ID token') ||
        errorMessage.includes('auth') ||
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('Authentication') ||
        err.networkError?.result?.errors?.some(e =>
          e.message?.includes('Firebase ID token') ||
          e.message?.includes('Unauthorized') ||
          e.extensions?.code === 'UNAUTHENTICATED'
        )) {
      return;
    }

    if (networkState?.online === false || !networkState?.online) {
      return;
    }
  }

  const storageEstimate = await getStorageEstimate();

  Sentry.captureException(err, {
    tags: {
      syncType,
      errorType: 'async-data-sync',
    },
    contexts: {
      syncOperation: {
        operation: `Async${syncType.charAt(0).toUpperCase() + syncType.slice(1)}`,
        statusCode: err.networkError?.statusCode,
        message: err.message,
        online: networkState?.online,
      },
      storage: storageEstimate,
    },
    level: 'error',
  });
};

export const reportSyncRecovery = async (syncType, reason) => {
  const storageEstimate = await getStorageEstimate();

  Sentry.captureMessage(`Self-healing triggered: ${syncType}`, {
    tags: {
      syncType,
      errorType: 'sync-self-healing',
      recoveryReason: reason,
    },
    contexts: {
      recovery: {
        syncType,
        reason,
        timestamp: new Date().toISOString(),
      },
      storage: storageEstimate,
    },
    level: 'warning',
    fingerprint: ['sync-self-healing', syncType],
  });
};

export const reportOfflineQueueError = (error, operation, storeName) => {
  try {
    Sentry.captureException(error, {
      tags: { errorType: 'offline-queue-write', storeName, queueErrorCode: error.code },
      extra: { operationId: operation?.id, operationType: operation?.type, storageError: error.cause?.name },
      fingerprint: ['offline-queue-write', storeName, error.code],
    });
  } catch (_) {}
};

export const reportOfflineSyncError = (err, operation, storeName, retryCount = 0) => {
  const isMaxRetries = retryCount >= 3;
  const level = isMaxRetries ? 'error' : 'warning';
  const diagnostics = operation.diagnostics || null;

  Sentry.captureException(err, {
    tags: {
      storeName,
      operationType: operation.type,
      errorType: 'offline-sync',
      maxRetriesReached: isMaxRetries.toString(),
      taskAction: diagnostics?.action || '',
      taskSource: diagnostics?.source || '',
      validTaskSubjectId:
        diagnostics?.isValidTaskSubjectId === undefined
          ? 'unknown'
          : String(diagnostics.isValidTaskSubjectId),
    },
    contexts: {
      offlineOperation: {
        id: operation.id,
        type: operation.type,
        createdAt: operation.createdAt,
        retryCount,
        customer: operation.customer,
        orderType: operation.orderType,
      },
      operationData: {
        variables: operation.variables,
        cartItems: operation.cartItems,
      },
      taskOperation: diagnostics,
    },
    level,
    fingerprint: [
      'offline-sync',
      storeName,
      operation.type,
      isMaxRetries ? 'max-retries' : 'retry',
    ],
  });
};

export const reportEventLogError = (err, eventData, context = {}) => {
  Sentry.captureException(err, {
    tags: {
      errorType: 'event-log',
      eventCode: eventData.eventCode,
    },
    contexts: {
      eventLog: {
        eventCode: eventData.eventCode,
        customer: eventData.customer,
        document: eventData.document,
        documentTable: eventData.documentTable,
        remark: eventData.remark,
        createdAt: eventData.createdAt,
        ...context,
      },
    },
    level: 'warning',
    fingerprint: ['event-log', eventData.eventCode],
  });
};

export const trackPerformanceMetric = (name, value, unit = 'millisecond') => {
  const transaction = Sentry.getActiveTransaction();
  if (transaction) {
    transaction.setMeasurement(name, value, unit);
  }
};

export const trackUserAction = (actionName, data = {}) => {
  Sentry.addBreadcrumb({
    category: 'user-action',
    message: actionName,
    level: 'info',
    data: {
      ...data,
      timestamp: new Date().toISOString(),
    },
  });
};

export const reportFirebaseTokenError = (err, context = {}) => {
  const isTimeout = err.message?.includes('timeout');
  const errorType = isTimeout ? 'firebase-token-timeout' : 'firebase-token-error';

  Sentry.captureException(err, {
    tags: {
      errorType,
      isTimeout: isTimeout.toString(),
    },
    contexts: {
      firebaseToken: {
        ...context,
        errorMessage: err.message,
        usedCachedToken: context.usedCachedToken || false,
        timestamp: new Date().toISOString(),
        networkType: navigator.connection?.effectiveType || 'unknown',
        online: navigator.onLine,
      },
    },
    level: isTimeout ? 'warning' : 'error',
    fingerprint: ['firebase-token', errorType],
  });
};

export const withPerformanceSpan = async (options, callback) => {
  return await Sentry.startSpan(
    {
      op: options.op,
      name: options.name,
      attributes: options.attributes || {},
    },
    callback
  );
};

let _lastManualSyncReport = 0;
const MANUAL_SYNC_REPORT_COOLDOWN_MS = 30 * 60 * 1000;

export const reportManualSyncAttempt = (operations = []) => {
  const now = Date.now();
  if (now - _lastManualSyncReport < MANUAL_SYNC_REPORT_COOLDOWN_MS) return;
  _lastManualSyncReport = now;

  const stores = operations.reduce((acc, op) => {
    if (op?.storeName) acc[op.storeName] = (acc[op.storeName] || 0) + 1;
    return acc;
  }, {});

  Sentry.captureMessage('Manual Sync Attempt', {
    level: 'info',
    tags: {
      type: 'manual-sync-attempt',
      operationCount: String(operations.length),
    },
    contexts: {
      syncAttempt: {
        operationCount: operations.length,
        stores,
        operations: operations.slice(0, 100).map((op) => ({
          id: op?.id,
          type: op?.type,
          storeName: op?.storeName,
          createdAt: op?.createdAt,
          taskDiagnostics: op?.diagnostics
            ? {
                action: op.diagnostics.action,
                source: op.diagnostics.source,
                rawTaskSubjectId: op.diagnostics.rawTaskSubjectId,
                rawTaskSubjectIdType: op.diagnostics.rawTaskSubjectIdType,
                numericTaskSubjectId: op.diagnostics.numericTaskSubjectId,
                isValidTaskSubjectId: op.diagnostics.isValidTaskSubjectId,
                taskId: op.diagnostics.taskId,
                taskShape: op.diagnostics.taskShape,
                taskArrayLength: op.diagnostics.taskArrayLength,
                customerId: op.diagnostics.customerId,
                customerCode: op.diagnostics.customerCode,
                oldStatusCode: op.diagnostics.oldStatusCode,
                newStatusCode: op.diagnostics.newStatusCode,
                route: op.diagnostics.route,
                capturedAt: op.diagnostics.capturedAt,
              }
            : null,
        })),
        timestamp: new Date().toISOString(),
      },
    },
    fingerprint: ['manual-sync-attempt'],
  });
};

let _lastSyncDiagnosticsReport = 0;
const SYNC_DIAGNOSTICS_COOLDOWN_MS = 60 * 60 * 1000;

export const sendSyncDiagnostics = async (syncKeys = [], authUser = null) => {
  const now = Date.now();
  if (now - _lastSyncDiagnosticsReport < SYNC_DIAGNOSTICS_COOLDOWN_MS) return;
  _lastSyncDiagnosticsReport = now;

  try {
    const productsStore = localforage.createInstance({ name: 'Products' });

    const [productsMeta, storage] = await Promise.all([
      productsStore.getItem('products_meta'),
      getStorageEstimate(),
    ]);

    Sentry.captureMessage('Sync Diagnostics', {
      level: 'info',
      tags: {
        type: 'sync-diagnostics',
        syncItems: syncKeys.join(','),
        userId: String(authUser?.id ?? ''),
        userCode: authUser?.code ?? '',
        channel: String(authUser?.channel ?? ''),
        productsTotal: String(productsMeta?.count ?? -1),
        posCount: String(productsMeta?.posCount ?? -1),
      },
      contexts: {
        diagnostics: {
          user: authUser ? {
            id: authUser.id,
            code: authUser.code,
            channel: authUser.channel,
            role: authUser.role,
            orgId: authUser.organization?.id,
            orgName: authUser.organization?.name,
          } : null,
          products: productsMeta || null,
          storage,
          device: {
            online: navigator.onLine,
            network: navigator.connection?.effectiveType || 'unknown',
          },
          timestamp: new Date().toISOString(),
        },
      },
      fingerprint: ['sync-diagnostics'],
    });
  } catch (_) {}
};
